export type TimetableDeleteEntity =
    | "teacher"
    | "subject"
    | "classroom"
    | "grade"
    | "place"
    | "assignment"
    | "timetable"
    | "teamTeaching";

export type TimetableDeleteResult = { ok: true } | { ok: false; message: string };

const ENTITY_LABELS: Record<TimetableDeleteEntity, string> = {
    teacher: "교사",
    subject: "과목",
    classroom: "학급",
    grade: "학년",
    place: "교실/특별실",
    assignment: "교육과정 배정",
    timetable: "시간표",
    teamTeaching: "공동수업 그룹",
};

const REF_TABLE_LABELS: Record<string, string> = {
    schedule_manager_schedule: "시간표 수업(생성된 시간표)",
    schedule_manager_teachingassignment: "교육과정 배정",
    schedule_manager_weeklyschedule: "주간 시간표",
    schedule_manager_teamteachinggroup: "공동수업 그룹",
    schedule_manager_timetable_schedules: "시간표 연결",
    schedule_manager_timetable: "시간표",
    schedule_manager_teacher_subjects: "교사-과목 연결",
    schedule_manager_teacherunavailabletime: "교사 불가 시간",
    schedule_manager_classroom: "학급",
    schedule_manager_gradeinfo: "학년 정보",
};

function referenceLabelFromDetails(details: string | null | undefined): string | null {
    if (!details) return null;
    for (const [table, label] of Object.entries(REF_TABLE_LABELS)) {
        if (details.includes(table)) return label;
    }
    return null;
}

function actionHint(entity: TimetableDeleteEntity, refLabel: string | null): string {
    if (entity === "timetable") {
        return "다른 데이터에서 이 시간표를 참조 중일 수 있습니다. 관련 수업·배정을 먼저 정리한 뒤 다시 시도해 주세요.";
    }
    const ref = refLabel ?? "다른 시간표 데이터";
    return (
        `먼저 「시간표 생성·관리」에서 해당 시간표를 삭제하거나, 「시간표 보기」에서 관련 수업을 제거한 뒤 다시 시도해 주세요.\n` +
        `(참조: ${ref})`
    );
}

type PostgrestLikeError = {
    code?: string;
    details?: string | null;
    message?: string;
    status?: number;
};

export function formatTimetableDeleteError(
    entity: TimetableDeleteEntity,
    error: PostgrestLikeError | null | undefined
): string {
    const label = ENTITY_LABELS[entity];
    const code = error?.code;
    const isFk =
        code === "23503" ||
        error?.status === 409 ||
        (error?.message?.includes("violates foreign key") ?? false);

    if (isFk) {
        const refLabel = referenceLabelFromDetails(error?.details ?? error?.message);
        return (
            `이 ${label}은(는) ${refLabel ?? "생성된 시간표 또는 배정"}에서 사용 중이라 삭제할 수 없습니다.\n\n` +
            actionHint(entity, refLabel)
        );
    }

    return `${label} 삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.`;
}

export function timetableDeleteOk(): TimetableDeleteResult {
    return { ok: true };
}

export function timetableDeleteFail(
    entity: TimetableDeleteEntity,
    error: PostgrestLikeError | null | undefined
): TimetableDeleteResult {
    return { ok: false, message: formatTimetableDeleteError(entity, error) };
}

export function alertTimetableDeleteError(e: unknown): void {
    alert(e instanceof Error ? e.message : "삭제에 실패했습니다.");
}
